import { useEffect } from 'react'
import { useTranslation } from 'react-i18next'

import { Button } from '@/design-system/Button'
import { LanguageSwitcher } from '@/design-system/LanguageSwitcher'
import { Container, Skeleton } from '@/design-system/primitives'
import { PublicLayout } from '@/layouts/PublicLayout'
import { ApiError } from '@/lib/apiClient'
import { applyEventTheme } from '@/lib/theme'

import { useEventContent, useEventSponsors } from '../api'
import { hasFixedPlace, siteText } from '../siteTexts'
import { useRubrics } from '../useRubrics'
import { Hero } from '../components/Hero'
import { DecorSection, PromiseSection } from '../components/HighlightSections'
import { RubricsSection } from '../components/RubricsSection'
import {
  ContactSection,
  ContentSection,
  PracticalSection,
  SponsorsSection,
  TicketsSection,
} from '../components/sections'

/** Le bandeau « masque obligatoire » a sa place dans le hero, pas en section. */
const MASK_POLICY_KEY = 'mask_policy'

/**
 * Deux blocs éditoriaux ont leur propre place sur l'accueil : la table qui
 * donne l'entrée, et le vote qui appartient à la salle. Ce sont les deux
 * choses qui décident quelqu'un à venir — les noyer dans la file commune
 * serait les perdre.
 */
const TABLE_KEY = 'table_admission'
const JURY_KEY = 'public_jury'
const PROMOTED = new Set([MASK_POLICY_KEY, TABLE_KEY, JURY_KEY])

/**
 * Ce que la page d'accueil garde, et ce qu'elle abandonne.
 *
 * Elle affichait onze sections : programme, gâteau, carte, tables, concours,
 * résultats, palmarès, FAQ… La page faisait plusieurs écrans avant d'atteindre
 * le bouton d'achat. Elle ne garde donc plus que l'essentiel — de quoi savoir
 * *quoi, quand, où* — puis mène au billet. Tout le reste vit dans sa propre
 * rubrique, où on va quand on le cherche.
 */
export function HomePage() {
  const { t } = useTranslation()

  // `useRubrics` sait quelles rubriques existent et construit le menu :
  // l'accueil et les pages de rubrique partagent ainsi la même barre.
  const { eventQuery, event, flags, navItems } = useRubrics()

  const contentQuery = useEventContent(event?.slug)
  const sponsorQuery = useEventSponsors(event?.slug)

  // L'identité visuelle de l'édition est une donnée : elle est appliquée
  // sur les variables CSS dès que l'événement est connu.
  useEffect(() => {
    if (event) applyEventTheme(event.theme)
  }, [event])

  useEffect(() => {
    if (!event) return
    document.title = [event.name, event.subtitle].filter(Boolean).join(' — ')
    document
      .querySelector('meta[name="description"]')
      ?.setAttribute('content', event.slogan || event.description)
  }, [event])

  const blocks = contentQuery.data ?? []
  const maskPolicy = blocks.find((block) => block.key === MASK_POLICY_KEY)
  const tablePromise = blocks.find((block) => block.key === TABLE_KEY)
  const juryPromise = blocks.find((block) => block.key === JURY_KEY)
  const sections = blocks.filter((block) => !hasFixedPlace(block.key, PROMOTED))
  const decor = siteText(blocks, 'site.decor')

  if (eventQuery.isPending) {
    return (
      <Container className="flex min-h-dvh flex-col justify-center gap-6 py-24">
        <Skeleton className="h-4 w-32" />
        <Skeleton className="h-16 w-full max-w-xl" />
        <Skeleton className="h-4 w-64" />
        <span className="sr-only">{t('state.loading')}</span>
      </Container>
    )
  }

  if (eventQuery.isError || !event) {
    // Deux causes très différentes : aucune édition publiée (situation normale
    // hors saison) ou API injoignable (incident). On ne les confond pas.
    const notPublished =
      eventQuery.error instanceof ApiError && eventQuery.error.code === 'EVENT_NOT_PUBLISHED'

    return (
      <Container className="flex min-h-dvh flex-col justify-center py-24">
        {/* Même sans édition, la page doit se lire dans sa langue. */}
        <div className="mb-10 flex items-center justify-between gap-4">
          <img src="/leyamo.png" alt="LeYAMO" className="h-14 w-14 rounded-full" />
          <LanguageSwitcher />
        </div>
        <h1 className="font-display text-display-lg text-content-primary">
          {notPublished ? t('state.noEventTitle') : t('state.errorTitle')}
        </h1>
        <p className="mt-4 max-w-xl text-body-lg text-content-secondary">
          {notPublished ? t('state.noEventBody') : t('state.errorBody')}
        </p>
        {!notPublished ? (
          <Button className="mt-8 self-start" onClick={() => void eventQuery.refetch()}>
            {t('state.retry')}
          </Button>
        ) : null}
      </Container>
    )
  }

  return (
    <PublicLayout
      eventName={event.name}
      logoUrl={event.logo_url}
      availableLocales={event.locales}
      navItems={navItems}
    >
      <Hero event={event} maskPolicy={maskPolicy} />

      <PracticalSection event={event} />

      <PromiseSection table={tablePromise} jury={juryPromise} />

      <DecorSection event={event} text={decor} />

      {/* Deux blocs éditoriaux au plus : le reste est dans « Infos ». */}
      {sections.slice(0, 2).map((block) => (
        <ContentSection key={block.key} block={block} />
      ))}

      <RubricsSection flags={flags} />

      <TicketsSection event={event} />
      <SponsorsSection sponsors={sponsorQuery.data ?? []} />
      <ContactSection event={event} />

      {/* Espace réservé à la barre d'achat collante du mobile. */}
      <div aria-hidden className="h-16 sm:hidden" />
    </PublicLayout>
  )
}
